// Transform database property fields to component-friendly format
/* eslint-disable */
export const transformProperty = (property) => {
  if (!property) return null;

  const parseArrayField = (value) => {
    if (!value) return [];
    if (Array.isArray(value)) return value;
    if (typeof value === "string") {
      try {
        const parsed = JSON.parse(value);
        return Array.isArray(parsed) ? parsed : [];
      } catch (e) {
        return [];
      }
    }
    if (typeof value === "object") {
      if (Array.isArray(value.data)) return value.data;
      if (Array.isArray(value.items)) return value.items;
      if (Array.isArray(value.files)) return value.files;
      if (Array.isArray(value.receiptData)) return value.receiptData;
    }
    return [];
  };

  // Parse images if it's a JSON string
  const images = parseArrayField(property.images);
  const videosRaw = parseArrayField(property.videos);
  const propertyImages = parseArrayField(property.property_images);
  const propertyVideos = parseArrayField(property.property_videos);
  const videos = videosRaw.length > 0 ? videosRaw : propertyVideos;

  const parseObjectField = (value) => {
    if (!value) return {};
    if (typeof value === "string") {
      try {
        const parsed = JSON.parse(value);
        return parsed && typeof parsed === "object" ? parsed : {};
      } catch (e) {
        return {};
      }
    }
    return typeof value === "object" ? value : {};
  };

  const propertyDocuments = parseObjectField(property.property_documents);
  const legacyLegalDocuments = parseObjectField(property.legal_documents);
  const documentSource =
    Object.keys(propertyDocuments).length > 0 ? propertyDocuments : legacyLegalDocuments;

  let legalDocuments = null;
  if (documentSource && Object.keys(documentSource).length > 0) {
    legalDocuments = documentSource;

    if (legalDocuments) {
      const formattedDocs = {};
      Object.keys(legalDocuments).forEach(docType => {
        // Handle if document is an array
        if (Array.isArray(legalDocuments[docType])) {
          formattedDocs[docType] = legalDocuments[docType].map(doc => {
            if (typeof doc === 'string') {
              const driveFileId = doc.split('/').pop();
              return {
                id: null,
                fileId: null,
                file_id: null,
                url: doc,
                path: doc,
                filename: driveFileId,
                originalName: driveFileId,
                name: driveFileId,
                driveFileId: driveFileId,
                uploadUrl: doc,
                gcsUrl: doc,
                type: null,
              };
            }
            // If doc is object, preserve its properties
            return {
              id: doc.id || null,
              fileId: doc.fileId || null,
              file_id: doc.file_id || null,
              drive_file_id: doc.drive_file_id || null,
              url: doc.url || doc.path,
              path: doc.path || doc.url,
              filename: doc.originalName || doc.filename || doc.name || doc.url?.split('/').pop(),
              originalName: doc.originalName || doc.filename || doc.name,
              name: doc.originalName || doc.filename || doc.name,
              driveFileId: doc.driveFileId || doc.url?.split('/').pop(),
              uploadUrl: doc.uploadUrl || null,
              gcsUrl: doc.gcsUrl || null,
              type: doc.type || null,
            };
          });
        } 
        // Handle if document is a single string (URL)
        else if (typeof legalDocuments[docType] === 'string' && legalDocuments[docType]) {
          const driveFileId = legalDocuments[docType].split('/').pop();
          formattedDocs[docType] = [{
            id: null,
            fileId: null,
            file_id: null,
            url: legalDocuments[docType],
            path: legalDocuments[docType],
            filename: driveFileId,
            originalName: driveFileId,
            name: driveFileId,
            driveFileId: driveFileId,
            uploadUrl: legalDocuments[docType],
            gcsUrl: legalDocuments[docType],
            type: null,
          }];
        }
        // Empty array if no document
        else {
          formattedDocs[docType] = [];
        }
      });

      if (!formattedDocs.mother_deed && formattedDocs.original_mother_deed) {
        formattedDocs.mother_deed = formattedDocs.original_mother_deed;
      }
      if (!formattedDocs.original_mother_deed && formattedDocs.mother_deed) {
        formattedDocs.original_mother_deed = formattedDocs.mother_deed;
      }

      legalDocuments = formattedDocs;
    }
  }

  // Prefer property_images ordering first for consistent primary media across views.
  const firstImage =
    propertyImages.find(item => !item || typeof item === 'string' || item.mediaType !== 'video') ||
    images.find(item => !item || typeof item === 'string' || item.mediaType !== 'video') ||
    null;

  return {
    ...property,
    // Map snake_case to camelCase for component compatibility
    title:
      property.property_caption || property.property_description || "Property",
    propertyType: property.property_type,
    propertySubType: property.property_sub_type,
    expectedPrice: parseFloat(property.expected_price) || null,
    minPrice: parseFloat(property.min_price) || null,
    perSqftPrice: parseFloat(property.per_sqft_price) || null,
    perCentPrice: parseFloat(property.per_cent_price) || null,
    currentAvailability: property.current_availability || property.currentAvailability,
    carpetArea: property.carpet_area ? parseInt(property.carpet_area) : null,
    plinthArea:
      parseInt(property.builtup_area_actual) ||
      parseInt(property.builtup_area_sanctioned) ||
      null,
    extentCents: parseFloat(property.land_extent) || null,
    location:
      property.city ||
      property.address_line1 ||
      property.area ||
      "Location not specified",
    image: firstImage,
    images: images,
    videos: videos,
    property_images: propertyImages,
    property_videos: propertyVideos,
    propertyImages: propertyImages,
    propertyVideos: propertyVideos,
    featured: property.is_featured === 1 || property.is_featured === true,
    bedrooms:
      parseInt(property.bedroom_count) || null,
    bathrooms: parseInt(property.bathrooms) || null,
    // Resolve Google Map coordinates from either the new location object or the old flat field.
    // property.location from backend is the coordinate object { mapLink, latitude, longitude };
    // we must extract before the display string overwrites it above.
    googleMapLocation: (() => {
      const loc = property.location;
      if (loc && typeof loc === 'object') {
        if (loc.mapLink) return loc.mapLink;
        if (loc.latitude && loc.longitude) return `${loc.latitude}, ${loc.longitude}`;
      }
      return property.google_map_location || null;
    })(),
    latitude: (() => {
      const loc = property.location;
      if (loc && typeof loc === 'object' && loc.latitude) return loc.latitude;
      return property.latitude || null;
    })(),
    longitude: (() => {
      const loc = property.location;
      if (loc && typeof loc === 'object' && loc.longitude) return loc.longitude;
      return property.longitude || null;
    })(),
    // Preserve raw location object so map view / edit form can access it directly.
    geoLocation: (property.location && typeof property.location === 'object')
      ? property.location
      : null,
    // Infrastructure details
    roadApproach: property.road_access || "N/A",
    roadType: property.road_type || "N/A",
    electricity:
      property.power_supply === "Yes" || property.power_supply === "Available",
    waterSource: property.water_source || "N/A",
    sewageType: property.sewage_type || "N/A",
    // Legal documents (checking if available)
    rtcAvailable: property.rtc === "Yes" || property.rtc === "Available",
    khataAvailable: property.khata === "Yes" || property.khata === "Available",
    saleDeedAvailable:
      property.sale_deed === "Yes" || property.sale_deed === "Available",
    fmbNaksheAvailable:
      property.fmb_nakshe === "Yes" || property.fmb_nakshe === "Available",
    ecAvailable: property.ec === "Yes" || property.ec === "Available",
    mutationAvailable:
      property.mutation === "Yes" || property.mutation === "Available",
    motherDeedAvailable:
      property.original_mother_deed === "Yes" ||
      property.original_mother_deed === "Available",
    // Legal documents files
    legalDocuments: legalDocuments,
    // Additional info
    otherDetails: property.property_description || property.address_description,
    anyOtherDetails: property.address_description,
    // Keep original fields too
    district: property.district,
    taluk: property.taluk,
    state: property.state || "Karnataka",
    address: property.address_line1 || property.city,
    landmark: property.landmark,
    propertyCode: property.property_code || property.property_id,
    sellingAs: property.selling_as,
  };
};

// Transform array of properties
export const transformProperties = (properties) => {
  if (!Array.isArray(properties)) return [];
  return properties.map(transformProperty);
};
